import { supabase } from '../config/supabase';
import { studioUser, saveStudioAsset } from './studioCloud';
import { DEFAULT_AO_SETTINGS, validateAOSettings } from '../geometry/ambientOcclusion';
import { DEFAULT_RENDER, validateRenderSettings } from '../geometry/renderCapture';
import { lookupColourCode } from '../utils/colourCodes.ts';
import { assertStudioCloudWrite } from './studioLocalMode';

const announce = () => {
  localStorage.setItem('hestia.studio.appearance-updated', String(Date.now()));
  window.dispatchEvent(new Event('hestia:appearance-updated'));
};
function visualSettings(value) {
  const settings = { environment: value.environment, environmentIntensity: value.environmentIntensity,
    viewportAO: value.viewportAO, aoSettings: value.aoSettings || DEFAULT_AO_SETTINGS };
  validateRenderSettings({ ...DEFAULT_RENDER, ...settings });
  validateAOSettings(settings.aoSettings);
  if (typeof settings.viewportAO !== 'boolean') throw new Error('Viewport AO must be on or off.');
  return settings;
}
export async function loadPublicVisualSettings() {
  const { data, error } = await supabase.from('door_studio_visual_settings').select('settings,hdri_path').eq('id', true).maybeSingle();
  if (error) throw error;
  return data ? { ...visualSettings(data.settings), environmentAssetPath: data.hdri_path || undefined } : null;
}
export async function savePublicVisualSettings(value) {
  assertStudioCloudWrite();
  const settings = visualSettings(value), user = await studioUser(true);
  let hdri_path = null;
  if (settings.environment.startsWith('custom:')) {
    const { readEnvironmentBlob, listEnvironments } = await import('../geometry/environmentLibrary');
    const metadata = (await listEnvironments()).find((item) => item.id === settings.environment);
    if (!metadata) throw new Error('Select an available HDRI before sharing these settings.');
    await saveStudioAsset(settings.environment, 'hdri', await readEnvironmentBlob(settings.environment), metadata, user.id);
    const asset = await supabase.from('door_studio_assets').select('path').eq('owner_id', user.id).eq('id', settings.environment).single();
    if (asset.error) throw asset.error;
    hdri_path = asset.data.path;
  }
  const { error } = await supabase.from('door_studio_visual_settings').upsert({ id: true, settings, hdri_path, updated_by: user.id, updated_at: new Date().toISOString() });
  if (error) throw error;
  announce();
}
export async function readPublicEnvironmentBlob(path) {
  const { data, error } = await supabase.storage.from('door-studio').download(path);
  if (error) throw error;
  return data;
}
export async function listStudioColours(publicOnly = false) {
  let query = supabase.from('door_studio_colours').select('id,system,code,name,hex,published').order('name');
  if (publicOnly) query = query.eq('published', true);
  else query = query.eq('owner_id', (await studioUser(true)).id);
  const { data, error } = await query;
  if (error) throw error;
  return data;
}
export async function saveStudioColour(value) {
  assertStudioCloudWrite();
  const match = ['RAL', 'NCS'].includes(value.system) && lookupColourCode(value.system, value.code);
  if (!match || typeof value.published !== 'boolean') throw new Error('Enter a valid RAL or NCS code and choose its visibility.');
  const name = value.name.trim() || match.name || match.code;
  if (name.length > 100) throw new Error('Colour names can be up to 100 characters.');
  const user = await studioUser(true);
  const row = { owner_id: user.id, system: match.system, code: match.code, hex: match.hex,
    name, published: value.published, updated_at: new Date().toISOString() };
  const query = value.id ? supabase.from('door_studio_colours').update(row).eq('id', value.id).eq('owner_id', user.id)
    : supabase.from('door_studio_colours').insert(row);
  const { data, error } = await query.select('id,system,code,name,hex,published').single();
  if (error) throw new Error(error.code === '23505' ? 'This code is already saved. Select it from your library to edit it.' : error.message);
  announce(); return data;
}
